import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  MinusCircleIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid'
import type { ComponentProps, ReactElement } from 'react'

import type { AppTestCaseGroup } from '../../common/components/ApplicationConfigPage/types'
import { Badge } from '../../common/designs'

interface Props {
  application: AppTestCaseGroup
}

type BadgeVariant = ComponentProps<typeof Badge>['variant']

interface StatusBadgeProps {
  variant: BadgeVariant
  title: string
  label: string
  className?: string
  rounded?: boolean
  icon?: ReactElement
}

const StatusBadge = ({
  variant,
  title,
  label,
  className,
  rounded = true,
  icon,
}: StatusBadgeProps) => (
  <span className='inline-block max-w-full' title={title}>
    <Badge
      variant={variant}
      size='s'
      rounded={rounded}
      className={['w-28 justify-center', className].filter(Boolean).join(' ')}
      {...(icon ? { icon } : {})}
    >
      {label}
    </Badge>
  </span>
)

const ApplicationRowStatus = ({ application }: Props) => {
  if (application.hasNoDataCollectionRules) {
    return (
      <StatusBadge
        variant='blue'
        title='No data collection rules are enabled for this application.'
        label='Not configured'
        icon={<InformationCircleIcon aria-hidden />}
        className='max-w-full truncate'
      />
    )
  }

  if (!application.isEnabled) {
    return (
      <StatusBadge
        variant='gray'
        title='This application is not enabled for data collection.'
        label='Disabled'
        icon={<MinusCircleIcon aria-hidden />}
        className='max-w-full truncate'
      />
    )
  }

  if (application.hasFailingTests) {
    return (
      <StatusBadge
        variant='red'
        title='One or more config tests are failing for this application.'
        label='Failing tests'
        icon={<XCircleIcon aria-hidden />}
        className='max-w-full truncate'
      />
    )
  }

  if (application.hasNotRunTests) {
    return (
      <StatusBadge
        variant='orange'
        title='Rules or tests changed since the last test run.'
        label='Tests not run'
        icon={<ExclamationCircleIcon aria-hidden />}
        className='max-w-full truncate'
      />
    )
  }

  return (
    <StatusBadge
      variant='green'
      title='Rules and tests look healthy for this application.'
      label='Configured'
      icon={<CheckCircleIcon aria-hidden />}
      className='max-w-full truncate'
    />
  )
}

export default ApplicationRowStatus
